//=================================================
// Lightbox
//=================================================

import { createInitializeOnce } from './utilities';
import { setupAudio } from './audio';
import { setupVideos } from './videos';
import { setupImageViewers } from './images';
import { hideAllTooltips, setupTooltips } from "./tooltips";

const initializeOnce = createInitializeOnce();

// Update the next/prev and count controls in the lightbox
function updateOverlayControls(overlay) {
    if (!overlay) {
        return;
    }

    const count = overlay.querySelector('.overlay-header__count');
    const prev = overlay.querySelector('.overlay-header__prev');
    const next = overlay.querySelector('.overlay-header__next');

    const index = Number(overlay.dataset.index);
    const gallery = overlay.dataset.gallery;
    const galleryItems = document.querySelectorAll(`.overlay-preview-wrapper[data-gallery="${ gallery }"]`);

    if (galleryItems.length > 1) {
        [prev, next, count].forEach((el) => {
            if (el === null) {
                return;
            }

            el.classList.add('active');
        });

        if (prev) prev.disabled = index < 1
        if (next) next.disabled = Number(index) + 1 === galleryItems.length;
        if (count) count.innerText = `${ index + 1 } / ${ galleryItems.length }`;
    } else {
        [prev, next, count].forEach((el) => {
            if (el === null) {
                return;
            }

            el.classList.remove('active');
            el.disabled = true;
        });
    }
}

// Load the lightbox from a given wrapper element
function loadOverlayContent(wrapper) {
    const overlay = document.querySelector('.overlay--media');
    if (overlay) {
        const overlayContent = overlay.querySelector('.overlay-content');
        const content = wrapper.querySelector('.overlay-content');

        // Set data values for reference
        const galleryItems = document.querySelectorAll(`.overlay-preview-wrapper[data-gallery="${ wrapper.dataset.gallery }"]`);
        overlay.dataset.index = Array.prototype.indexOf.call(galleryItems, wrapper).toString();
        overlay.dataset.gallery = wrapper.dataset.gallery;

        // Replace existing content with new content clone
        hideAllTooltips();
        const newOverlayContent = content.cloneNode(true);
        overlayContent.replaceWith(newOverlayContent);

        // Sync the caption language toggle with the dialog's persisted language state
        newOverlayContent.querySelector('.overlay-caption-toggle')?.setAttribute('aria-checked', overlay.classList.contains('overlay--es'));

        // The clone is composed of fresh element objects, so the identity-based
        // initializeOnce guards in the setup calls below re-initialize it automatically.
        setupAudio();
        setupTooltips();
        setupVideos();
        setupImageViewers();
        updateOverlayControls(overlay);

        overlay.showModal();
        overlay.focus();
    }
}

export default function setupOverlays() {
    // Media lightboxes
    document.querySelectorAll('.overlay-preview-wrapper').forEach((el) => {
        if (initializeOnce(el)) {
            el.querySelector('.overlay-preview')?.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Opening adopts the page's language; prev/next keeps the dialog's own state
                document.querySelector('.overlay--media')?.classList.toggle('overlay--es', el.dataset.locale === 'es');

                loadOverlayContent(el);
            });
        }
    });

    // Generic lightboxes
    document.querySelectorAll('.overlay-wrapper').forEach((el) => {
        if (initializeOnce(el)) {
            const overlay = el.querySelector('.overlay');
            const toggle = el.querySelector('.overlay__toggle');

            if (toggle) {
                toggle.addEventListener('click', (e) => {
                    overlay.showModal();
                });
            }
        }
    });

    // The actual overlay modals
    document.querySelectorAll('.overlay').forEach((overlay) => {
        if (initializeOnce(overlay)) {
            // Close overlay if click is outside of it or a close button
            overlay.addEventListener('click', (e) => {
                if (e.target.matches('.overlay-close, .overlay-close *')) {
                    overlay.close();
                    return;
                }

                // Keyboard-triggered clicks report (0,0) coordinates in Firefox
                if (e.detail === 0) return;

                const rect = overlay.getBoundingClientRect();
                const insideOverlay = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
                    rect.left <= e.clientX && e.clientX <= rect.left + rect.width);

                if (!insideOverlay) {
                    overlay.close();
                }
            });

            // Caption language toggle (delegated because the button lives in cloned content)
            overlay.addEventListener('click', (e) => {
                const toggle = e.target.closest('.overlay-caption-toggle');
                if (toggle) {
                    toggle.setAttribute('aria-checked', overlay.classList.toggle('overlay--es'));
                }
            });

            // Previous
            const prev = overlay.querySelector('.overlay-header__prev');
            if (prev) {
                prev.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const galleryItems = document.querySelectorAll(`.overlay-preview-wrapper[data-gallery="${ overlay.dataset.gallery }"]`);
                    if (galleryItems) {
                        const prevItem = galleryItems[Number(overlay.dataset.index) - 1];
                        if (prevItem) {
                            loadOverlayContent(prevItem);
                        }
                    }
                })
            }

            // Next
            const next = overlay.querySelector('.overlay-header__next');
            if (next) {
                next.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const galleryItems = document.querySelectorAll(`.overlay-preview-wrapper[data-gallery="${ overlay.dataset.gallery }"]`);
                    if (galleryItems) {
                        const nextItem = galleryItems[Number(overlay.dataset.index) + 1];
                        if (nextItem) {
                            loadOverlayContent(nextItem);
                        }
                    }
                });
            }

            // Handle keyboard controls
            // This can get messed up by OpenSeadragon viewers
            overlay.addEventListener('keydown', (e) => {
                // Don't hijack arrow keys from interactive elements (sliders, scrubbers, inputs)
                if (e.target.closest('input, textarea, select, audio, video, [role="slider"]')) return;

                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    overlay.querySelector('.overlay-header__next')?.click();
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    overlay.querySelector('.overlay-header__prev')?.click();
                }
            });
        }
    });

}
